import {
  MarkdownViewer,
  NOTE_CONTENT_CLASSES,
} from "@client/components/round/allocator/MarkdownViewer";
import type { MarkdownComponents } from "@client/components/round/allocator/MarkdownViewerImpl";
import { cn } from "@client/utils/cn";

/**
 * The prose an admin writes into a form — its description, a section's, a
 * question's help text (which is also a text block's whole body) — rendered
 * for reading.
 *
 * These fields take markdown. They used to be printed as one paragraph with
 * its line breaks kept, which is exactly what a Google Forms import looks
 * like: "Eligible Entities" was a heading only to someone who already knew
 * it was, and a link was the words of a link. The same renderer the notes
 * and research features use does the work here, with two decisions layered
 * on top:
 *
 *  - **Every heading level is the same subhead.** The form owns its own
 *    hierarchy — form title, section title, question — and a description
 *    sits under all of it, so `#`, `##` and `###` all become one modest
 *    style: the surrounding text's size, semibold, in the primary colour.
 *    An admin can't out-shout the section they're writing in. They are
 *    still real headings in the accessibility tree, so a screen reader can
 *    walk "General Information", "Eligible Entities" the way a sighted
 *    reader scans them.
 *  - **Images are dropped.** Nothing in a form should load from an admin's
 *    URL into an applicant's browser.
 *
 * What comes free from the shared pipeline and matters here: a single
 * newline is still a line break, so everything already stored renders as it
 * did before; bare URLs become links; links open in a new tab; raw HTML is
 * sanitised away.
 *
 * Sizing and colour are the caller's, as one of the shared typography
 * classes plus `text-muted-foreground` — the same contract as the quiet
 * fields the text is written in, so the rendered and edited forms of a field
 * line up.
 */

// Module-level so the parsed output stays cached across re-renders — the
// viewer memoises on its `components` identity.
const subhead = ({
  node: _node,
  children,
}: {
  node?: unknown;
  children?: React.ReactNode;
}) => (
  <h4 className="mt-3 mb-0.5 font-semibold text-text-primary first:mt-0">
    {children}
  </h4>
);

// A link drawn as one but going nowhere: for prose shown inside a control,
// where a real anchor would be an interactive element nested in another —
// reachable by Tab, activated by the Enter that was meant to open the
// editor. Not `pointer-events-none`, which hides it from the mouse only.
const inertLink = ({
  node: _node,
  children,
}: {
  node?: unknown;
  children?: React.ReactNode;
}) => (
  <span className="text-accent underline underline-offset-2">{children}</span>
);

const FORM_PROSE_COMPONENTS: MarkdownComponents = {
  h1: subhead,
  h2: subhead,
  h3: subhead,
  h4: subhead,
  h5: subhead,
  h6: subhead,
  img: () => null,
  // GFM task lists (`- [ ] item`) would put a disabled checkbox in a form
  // that is about to ask real questions with real checkboxes.
  input: () => null,
};

const INERT_FORM_PROSE_COMPONENTS: MarkdownComponents = {
  ...FORM_PROSE_COMPONENTS,
  a: inertLink,
};

export const FORM_PROSE_CLASSES = cn(
  NOTE_CONTENT_CLASSES,
  "break-words [&_a]:text-accent [&_a]:underline [&_a]:underline-offset-2",
  "[&_strong]:font-semibold [&_strong]:text-text-primary",
);

export function FormProse({
  value,
  className,
  links = "live",
}: {
  value: string;
  className?: string;
  /**
   * `inert` when the prose is the face of a control (the builder's resting
   * field): links look like links but are not elements of their own, so the
   * control has no interactive descendants. Everything else is unchanged.
   */
  links?: "live" | "inert";
}) {
  return (
    <div className={cn(FORM_PROSE_CLASSES, className)}>
      <MarkdownViewer
        value={value}
        components={
          links === "inert"
            ? INERT_FORM_PROSE_COMPONENTS
            : FORM_PROSE_COMPONENTS
        }
      />
    </div>
  );
}
